import { WorkflowStatus } from './WorkflowStatus';

/**
 * `savedSearchId` of the New Bill attribution written when AI tags put a bill in What's New.
 * AI tagging reuses the saved-search attribution shape, so the web uses this id to show the
 * source as AI tags rather than as a link to a saved search.
 */
export const AI_TAGS_ATTRIBUTION_ID = 'ai-tags';

const AI_TAGS_SOURCE_NAME_PREFIX = 'AI tags: ';

/** The source name of an AI-tags New Bill attribution, e.g. "AI tags: Privacy, Tax". */
export const formatAiTagsSourceName = (tags: string[]): string =>
  `${AI_TAGS_SOURCE_NAME_PREFIX}${tags.join(', ')}`;

/** The tags in an AI-tags source name. Topic names never contain ", ". */
export const parseAiTagsSourceName = (name: string): string[] =>
  name.startsWith(AI_TAGS_SOURCE_NAME_PREFIX)
    ? name.slice(AI_TAGS_SOURCE_NAME_PREFIX.length).split(', ').filter(Boolean)
    : [];

/**
 * The classifier's fixed topic list, kept in sync with
 * data/insights-shared/insights_shared/bill_topics/topics.yml. A rule's tag must be one of
 * these names, because the classifier only ever returns them.
 */
export const AUTO_TRIAGE_TOPICS = [
  'Youth Safety',
  'AI (General)',
  'AI Chatbots',
  'E-Commerce',
  'Privacy',
  'CSAM',
  'Tax',
  'Addictive Feeds for Minors',
  'Age Verification and Trust & Safety',
  'Nonconsensual Intimate Images',
  'App-Level Age Verification',
  'Product Advertising',
  'Warning Labels',
];

/** Conditions a bill must meet (any one of) before it reaches the dashboard. */
export enum AutoTriageThreshold {
  PassedChamber = 'passed_chamber',
  CommitteePassage = 'committee_passage',
}

export const AUTO_TRIAGE_THRESHOLD_LABELS: Record<AutoTriageThreshold, string> = {
  [AutoTriageThreshold.PassedChamber]: 'Passed one chamber',
  [AutoTriageThreshold.CommitteePassage]: 'Passed out of committee',
};

/**
 * Bills with AI tag `tag` from one of `jurisdictionIds` (any jurisdiction when empty) are
 * added to workspace `teamId`; rule actions then assign `assigneeUserIds`, set
 * `workflowStatus` and track the bill when `trackBills` is set.
 */
export interface AutoTriageRule {
  tag: string;
  jurisdictionIds: number[];
  teamId: number;
  assigneeUserIds: number[];
  workflowStatus: WorkflowStatus;
  trackBills: boolean;
}

export interface AutoTriageSettingsInput {
  rules: AutoTriageRule[];
  thresholds: AutoTriageThreshold[];
  /** When false, rule actions wait for someone to click Auto Triage in What's New. */
  applyActionsAutomatically: boolean;
}

export interface AutoTriageSettings extends AutoTriageSettingsInput {
  organizationId: number;
  updatedByUserId: number | null;
  createdAt: string;
  updatedAt: string;
}

export enum AutoTriagePlacementState {
  /** Placed as a new bill because it met an alert threshold, or none are set. */
  New = 'new',
  /** Placed as Monitoring and kept off What's New until it meets an alert threshold. */
  Held = 'held',
  /** Was held, then met an alert threshold and moved to Active Review. */
  Released = 'released',
  /** Was held until a person changed its Workflow Status; Auto Triage no longer manages it. */
  Ended = 'ended',
}

/** Which pending placements an Auto Triage click applies rule actions to. */
export enum AutoTriageApplyScope {
  New = 'new',
  Released = 'released',
}

/** How many placements an Auto Triage click by the user would apply rule actions to. */
export type AutoTriagePendingCounts = Record<AutoTriageApplyScope, number>;

export interface AutoTriageWorkspaceCount {
  teamId: number;
  teamName: string;
  billCount: number;
}

/** What resetting an Auto Triage demo organization removed and placed again. */
export interface AutoTriageDemoResetResult {
  dryRun: boolean;
  /** Bills whose Auto Triage changes were removed. */
  clearedBillCount: number;
  /** Bills placed again from their stored AI tags; 0 on a dry run. */
  placedBillCount: number;
  /** Rows removed or changed, by kind. */
  counts: Record<string, number>;
}

/** The tagging run a demo reset starts for the demo bill list. */
export interface AutoTriageDemoTaggingSummary {
  /** Sent to the classifier; each is placed when its tags come back. */
  queuedCount: number;
  /** Already classified, so placed right away by their stored tags, if any match a rule. */
  reusedCount: number;
  /** Pending in another run, or failed before. */
  skippedCount: number;
  /** Not among this environment's bills. */
  unknownCount: number;
  /** Could not be queued; the next reset sends them again. */
  queueFailedCount: number;
}

export interface AutoTriageDemoResetResponse extends AutoTriageDemoResetResult {
  /** Null on a dry run, when the demo bill list is empty, or without Auto Triage settings. */
  tagging: AutoTriageDemoTaggingSummary | null;
}

/** A held bill a demo's simulated update can release. */
export interface AutoTriageDemoHeldBill {
  billId: string;
  /** e.g. "CA AB 2212" */
  billName: string;
  teamId: number;
  teamName: string;
}

export interface AutoTriageDemoSimulateResult {
  /** The simulated bill actions added, which the demo reset removes. */
  actionIds: number[];
  release: AutoTriageReleaseResult;
}

export interface AutoTriageApplyResult {
  /** Shared by the AI Activity Log entries this click wrote; null when nothing was pending. */
  batchId: string | null;
  appliedCount: number;
  failedBillIds: string[];
  byWorkspace: AutoTriageWorkspaceCount[];
}

export interface AutoTriageHeldCount {
  /** Bills held in at least one of the organization's workspaces. */
  heldBillCount: number;
}

export interface AutoTriageReleaseResult {
  /** Shared by the AI Activity Log entries this check wrote; null when nothing was released. */
  batchId: string | null;
  releasedCount: number;
  /** Held bills a person moved to another status; Auto Triage no longer manages them. */
  endedCount: number;
  stillHeldCount: number;
}
